import * as z from "zod";

export const ProofSkippedMetadata = z.object({
  repository: z.string(),
  pr_number: z.number(),
  pr_url: z.string(),
  head_sha: z.string(),
  reason: z.enum([
    "user_visible_change",
    "risky_change",
    "test_only",
    "docs_only",
    "trivial",
    "other",
    "repository_rules",
    "org_guidance",
    "unavailable",
    "no_changes",
    "formatting_only",
    "generated_only",
    "tests_or_docs_only",
  ]),
  probability: z.number().nullable(),
  model_id: z.string().nullable(),
});
export type ProofSkippedMetadata = z.infer<typeof ProofSkippedMetadata>;
export type ProofSkippedReason = ProofSkippedMetadata["reason"];

export const PROOF_SKIP_REASON_LABELS: Record<ProofSkippedReason, string> = {
  user_visible_change: "not worth a run",
  risky_change: "not worth a run",
  other: "not worth a run",
  test_only: "test-only change",
  docs_only: "docs-only change",
  trivial: "trivial change",
  repository_rules: "ruled out by .blacksmith/PROOF.md",
  org_guidance: "ruled out by the org's Proof guidance",
  unavailable: "[code]smith could not decide",
  no_changes: "no changes since the last proof",
  formatting_only: "formatting-only change",
  generated_only: "generated files only",
  tests_or_docs_only: "test or docs change during a run",
};

const parsedByRaw = new WeakMap<object, ProofSkippedMetadata | null>();

export function proofSkippedMetadata(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): ProofSkippedMetadata | null {
  if (message.role !== "agent") return null;
  const raw = message.metadata?.proof_skipped;
  if (typeof raw !== "object" || raw === null) return null;
  let parsed = parsedByRaw.get(raw);
  if (parsed === undefined) {
    const result = ProofSkippedMetadata.safeParse(raw);
    parsed = result.success ? result.data : null;
    parsedByRaw.set(raw, parsed);
  }
  return parsed;
}

export function isProofSkippedMessage(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return proofSkippedMetadata(message) !== null;
}

/**
 * A skip with no verdict behind it (the decision call failed) is a [code]smith
 * hiccup, not news about the PR: the transcript hides it, and the backend
 * keeps the row only so a retried job does not pay for a second decision.
 */
export function isUndecidedProofSkipMessage(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return proofSkippedMetadata(message)?.reason === "unavailable";
}
